<template>
	<div class="map-page">
		<div :class="mapContentClasses" @pointerleave="unhoverAnyThatIsHovered">
			<div ref="mapRoot" class="map-root" />
			<GnssTooltipsOverlay
				v-if="userStore.allowToChangeMapMode"
				:display-items="gnssTooltipsOverlayDisplayItems"
				:hovered-id="hoveredOverlayId"
				:mapMode="baseStationsStore.mode"
				:showGridpointsOnly="showGridpointsOnly"
				:allSubnetworksMode="allSubnetworksMode"
			/>
			<MapHoverTooltip
				:hoveredTyped="hoveredTyped"
				:enabled="mapHoverPopupEnabled"
				:tooltipStyle="tooltipStyle"
				:mapMode="baseStationsStore.mode"
			/>
			<VrsAccuracySummaryPanel v-if="vrsAccuracySummary != null" :summary="vrsAccuracySummary" />
			<MapModeChooser v-if="showMapLegends && userStore.allowToChangeMapMode && !isCcmMapMode(baseStationsStore.mode)" />
			<MapLegend
				v-if="showMapLegends && !isCcmMapMode(baseStationsStore.mode)"
				v-model:measureEnabled="measureEnabled"
				v-model:fillCoverageArea="fillCoverageArea"
				v-model:showBaseStationTooltips="showBaseStationTooltips"
				v-model:qualityColorMode="qualityColorMode"
				v-model:showGridpointsOnly="showGridpointsOnly"
				v-model:gnssSelected="gnssSelected"
				:gnssTooltipsNonNullGnss="gnssTooltipsNonNullGnss"
				:bsFilterOptions="bsFilterOptions"
				v-model:bsFilterSelected="bsFilterSelected"
				:bsSnFilterOptions="bsSnFilterOptions"
				v-model:bsSnFilterSelected="bsSnFilterSelected"
				:activeSubNetworks="activeSubNetworks"
			/>
			<MapLegendCCM
				v-else-if="showMapLegends && isCcmMapMode(baseStationsStore.mode) && drawModeCCM !== CCM_MODE.list"
				:editMode="editModeCCM"
				v-model:step="stepCCM"
				v-model:drawMode="drawModeCCM"
				v-model:title="titleCCM"
				:selectedData="selectedCCMData"
				:cNetConstructorSaveAndApply="cNetConstructorSaveAndApply"
				:subGridCount="subGridCountCCM"
				:subGridSelectedCounts="subGridSelectedCountsCCM"
				:showSubgridsUi="showSubgridsUiCCM"
				:updateAllGridpointsStatuses="updateAllGridpointsStatuses"
				v-model:currentSubGrid="currentSubGridCCM"
				:allowDeleteSubGrid="allowDeleteSubGridCCM"
				:subGridsOptions="subGridsOptionsCCM"
				:addSubGrid="addSubGridCCM"
				:deleteSubGrid="deleteSubGridCCM"
				v-model:showOtherGrids="showOtherGridsCCM"
			/>
			<MapLegendOtherGridsCCM
				v-if="showMapLegends && isCcmMapMode(baseStationsStore.mode) && drawModeCCM !== CCM_MODE.list && showOtherGridsCCM"
				:otherGridsList="otherGridsListCCM"
				v-model:selectedOtherGridsKeys="selectedOtherGridsKeysCCM"
				:isLoading="otherGridsLoadingCCM"
			/>
		<!-- map-content -->
		</div>
	<!-- map-page -->
	</div> 
</template>

<script lang="ts">
import { ref, computed } from 'vue';

import Consts from '@/api/Consts';

import { useRoute } from 'vue-router';

import { useBaseStationsStore } from '@/stores/baseStations';
import { MAP_MODE } from '@/components/map/consts/mapFeatureConsts';
import type { MapMode } from '@/components/map/types/mapFeatureTypes';
import { useUserStore } from '@/stores/user';

import { useGnssOptions } from '@/components/stats/useGnssOptions';
import { useMapBsFilter } from '@/components/map/utils/useMapBsFilter';
import { useMapMainSubNetworkFilter } from '@/components/map/utils/useMapMainSubNetworkFilter';
import { useMapContainer } from '@/components/map/useMapContainer';
import { CCM_MODE } from '@/components/map/cnetConstructorMode/ccmConsts';

import MapModeChooser from '@/components/map/MapModeChooser.vue';
import GnssTooltipsOverlay from '@/components/map/tooltips/GnssTooltipsOverlay.vue';
import MapHoverTooltip from '@/components/map/tooltips/MapHoverTooltip.vue';
import VrsAccuracySummaryPanel from '@/components/map/tooltips/VrsAccuracySummaryPanel.vue';

import MapLegend from '@/components/map/MapLegend.vue';
import MapLegendCCM from '@/components/map/cnetConstructorMode/MapLegendCCM.vue';
import MapLegendOtherGridsCCM from '@/components/map/cnetConstructorMode/MapLegendOtherGridsCCM.vue';

import MapConsts from '@/components/map/consts/MapConsts';

import 'ol/ol.css';


const isCcmMapMode = (mode: MapMode): boolean => (
	mode === MAP_MODE.cnetConstructor || mode === MAP_MODE.roversHistory
);

export default {
	components: {
		MapModeChooser, GnssTooltipsOverlay, MapHoverTooltip, VrsAccuracySummaryPanel,
		MapLegend, MapLegendCCM, MapLegendOtherGridsCCM,
	},
	setup() {
		const mapRoot = ref<HTMLElement>();
		const { gnssOptions, gnssSelected: gnssSelectedRaw } = useGnssOptions(false, true);
		const gnssSelected = computed({
			get(): string {
				const value = gnssSelectedRaw.value;
				return typeof value === 'string' ? value : '';
			},
			set(value: string) {
				gnssSelectedRaw.value = value;
			},
		});
		const bsFilterData = useMapBsFilter();
		const bsSnFilterData = useMapMainSubNetworkFilter();
		const {
			showGridpointsOnly,
			measureEnabled,
			fillCoverageArea,
			showBaseStationTooltips,
			qualityColorMode,
			mapHoverPopupEnabled,
			gnssTooltipsOverlayDisplayItems,
			gnssTooltipsNonNullGnss,
			isMapHovered,
			hoveredTyped,
			hoveredFeatureData,
			tooltipStyle,
			activeSubNetworks,
			allSubnetworksMode,
			vrsAccuracySummary,
			mapDataCCM,
			unhoverAnyThatIsHovered,
		} = useMapContainer(
			mapRoot,
			gnssSelected,
			bsFilterData.selected,
			bsSnFilterData.selected,
			bsSnFilterData.baseStationsInSN,
		);

		const baseStationsStore = useBaseStationsStore();
		const userStore = useUserStore();

		const mapContentClasses = computed((): Record<string, boolean> => ({
			'map-content': true,
			'map-content-pointer': isMapHovered.value,
			'map-content-crosshair': mapDataCCM.drawMode.value === CCM_MODE.selectAnchorPoint,
		}));

		const route = useRoute();
		const hoveredOverlayId = computed((): number | null => {
			const data = hoveredFeatureData.value;
			if (data == null) {
				return null;
			}
			return typeof data.id === 'number' ? data.id : null;
		});
		const showMapLegends = computed((): boolean => MapConsts.isMapFocusedRoute(route.name));
		return {
			mapRoot,
			showMapLegends,
			mapContentClasses,
			gnssOptions,
			gnssSelected,
			bsFilterOptions: bsFilterData.options,
			bsFilterSelected: bsFilterData.selected,
			bsSnFilterOptions: bsSnFilterData.options,
			bsSnFilterSelected: bsSnFilterData.selected,
			showGridpointsOnly,
			measureEnabled,
			fillCoverageArea,
			showBaseStationTooltips,
			qualityColorMode,
			mapHoverPopupEnabled,
			gnssTooltipsOverlayDisplayItems,
			gnssTooltipsNonNullGnss,
			hoveredTyped,
			hoveredOverlayId,
			tooltipStyle,
			baseStationsStore,
			userStore,
			vrsServiceColor: Consts.COLORS.vrsServiceColor,
			activeSubNetworks,
			allSubnetworksMode,
			vrsAccuracySummary,
			unhoverAnyThatIsHovered,

			drawModeCCM: mapDataCCM.drawMode,
			editModeCCM: mapDataCCM.editMode,
			stepCCM: mapDataCCM.step,
			titleCCM: mapDataCCM.title,
			selectedCCMData: mapDataCCM.selectedData,
			cNetConstructorSaveAndApply: mapDataCCM.cNetConstructorSaveAndApply,
			subGridCountCCM: mapDataCCM.subGridCount,
			subGridSelectedCountsCCM: mapDataCCM.subGridSelectedCounts,
			showSubgridsUiCCM: mapDataCCM.showSubgridsUi,
			updateAllGridpointsStatuses: mapDataCCM.updateAllGridpointsStatuses,
			currentSubGridCCM: mapDataCCM.currentSubGrid,
			allowDeleteSubGridCCM: mapDataCCM.allowDeleteSubGrid,
			subGridsOptionsCCM: mapDataCCM.subGridsOptions,
			addSubGridCCM: mapDataCCM.addSubGrid,
			deleteSubGridCCM: mapDataCCM.deleteSubGrid,
			showOtherGridsCCM: mapDataCCM.showOtherGrids,
			otherGridsListCCM: mapDataCCM.otherGridsList,
			selectedOtherGridsKeysCCM: mapDataCCM.selectedOtherGridsKeys,
			otherGridsLoadingCCM: mapDataCCM.otherGridsLoading,

			CCM_MODE,
			isCcmMapMode,
		}
	},
}
</script>

<style scoped>
@import '@/assets/map-tooltip.css';

.map-page {
	display: flex;
	width: 100%;
	height: 100%;
}

.map-content {
	flex-grow: 5;
	height: 100%;
}

.map-content-crosshair {
	cursor: crosshair;
}

.map-content-pointer {
	cursor: pointer;
}

.map-root {
	width: 100%;
	height: 100%;
}
</style>

<style>
.ol-zoom {
	inset-inline-start: auto;
	inset-inline-end: 8px;
	inset-block-start: 96px;
}

.map-tooltip-header-vrs {
	background-color: v-bind(vrsServiceColor);
}

.ol-overlaycontainer {
	direction: ltr;
}

.ol-tooltip {
	position: relative;
	background: rgba(0, 0, 0, 0.5);
	border-radius: 4px;
	color: white;
	padding: 4px 8px;
	opacity: 0.7;
	white-space: nowrap;
	font-size: 12px;
	cursor: default;
	user-select: none;
	opacity: 1;
	font-weight: bold;
}

.ol-tooltip-static {
	background-color: black;
}

.ol-tooltip-measure:before,
.ol-tooltip-static:before {
	border-block-start: 6px solid rgba(0, 0, 0, 0.5);
	border-inline-end: 6px solid transparent;
	border-inline-start: 6px solid transparent;
	content: "";
	position: absolute;
	bottom: -6px;
	margin-inline-start: -7px;
	left: 50%;
}

.ol-tooltip-static:before {
	border-top-color: black;
}
</style>
